import { useEffect, useRef, useState } from 'react';

import type { AvisoDaTela } from '../../participantes/ui/Aviso';
import { alterarRanking, listarRankings } from '../api/ranking';

/** Falhas seguidas de leitura da mesma turma até mandar o admin ao painel do facilitador (`AC-RA-065`). */
const FALHAS_ATE_O_FACILITADOR = 3;

/**
 * O toggle do ranking da turma na Operação (`RA2c`): verde liberado, vermelho travado. Vale para a
 * turma inteira, e só troca depois que o painel grava (`RN-RA-050`). Se a leitura falha, mostra
 * "Ranking indisponível" e o clique relê; na 3.ª falha seguida, o aviso aponta o painel do
 * facilitador, que também libera o ranking da turma.
 */
export function RankingDaTurma({ turmaId, csrfToken, aoAvisar }: {
    turmaId: string;
    csrfToken: string;
    aoAvisar: (aviso: AvisoDaTela) => void;
}) {
    const [liberado, setLiberado] = useState<boolean | null>(null);
    const [indisponivel, setIndisponivel] = useState(false);
    const [leitura, setLeitura] = useState(0);
    const [gravando, setGravando] = useState(false);
    const falhas = useRef({ turmaId, seguidas: 0 });

    useEffect(() => {
        let ativo = true;
        if (falhas.current.turmaId !== turmaId) falhas.current = { turmaId, seguidas: 0 };
        setLiberado(null);
        setIndisponivel(false);
        listarRankings()
            .then((turmas) => {
                if (!ativo) return;
                falhas.current.seguidas = 0;
                setLiberado(Array.isArray(turmas) && turmas.some((turma) => turma.turma_id === turmaId && turma.ranking));
            })
            .catch(() => {
                if (!ativo) return;
                falhas.current.seguidas += 1;
                setIndisponivel(true);
                aoAvisar({
                    tipo: 'erro',
                    texto: falhas.current.seguidas >= FALHAS_ATE_O_FACILITADOR
                        ? `O ranking da turma ${turmaId} continua indisponível. Use o painel do facilitador para liberar ou travar o ranking.`
                        : `Não foi possível ler o ranking da turma ${turmaId}. Tente novamente.`,
                });
            });
        return () => {
            ativo = false;
        };
    }, [turmaId, leitura, aoAvisar]);

    async function alternar() {
        if (indisponivel) {
            setLeitura((n) => n + 1);
            return;
        }
        if (liberado === null) return;
        const novo = !liberado;
        setGravando(true);
        try {
            const gravado = await alterarRanking(turmaId, novo, csrfToken);
            setLiberado(gravado.ranking);
            if (gravado.tablets_avisados === false) {
                // Gravou, mas o jogo não avisou os tablets agora: eles releem ao reconectar.
                aoAvisar({
                    tipo: 'erro',
                    texto: `Ranking ${gravado.ranking ? 'liberado' : 'travado'} para a turma ${turmaId}, mas os tablets não foram avisados agora: eles atualizam quando reconectarem.`,
                });
                return;
            }
            aoAvisar({
                tipo: 'sucesso',
                texto: gravado.ranking ? `Ranking liberado para todos da turma ${turmaId}.` : `Ranking travado para a turma ${turmaId}.`,
            });
        } catch {
            aoAvisar({ tipo: 'erro', texto: `Não foi possível alterar o ranking da turma ${turmaId}.` });
        } finally {
            setGravando(false);
        }
    }

    return (
        <button
            type="button"
            role="switch"
            aria-label="Ranking da turma"
            aria-checked={liberado === true}
            className={`toggle-ranking ${indisponivel ? 'toggle-ranking-indisponivel' : liberado ? 'toggle-ranking-liberado' : 'toggle-ranking-travado'}`}
            title={indisponivel ? 'Clique para tentar ler de novo' : undefined}
            disabled={(liberado === null && !indisponivel) || gravando}
            onClick={() => void alternar()}
        >
            <span className="trilho-toggle-ranking" aria-hidden="true"><span className="botao-toggle-ranking" /></span>
            {indisponivel ? 'Ranking indisponível' : liberado ? 'Ranking liberado' : 'Ranking travado'}
        </button>
    );
}
